import { computed, ref } from 'vue';
import {
  ensureStorageAccess,
  hasEssentialStorageConsent,
  isStorageBlockedError,
  markEssentialStorageConsent,
  probeLocalStorage,
  requireStorageForLogin,
} from '@/utils/client-storage';
import { startPrivyWarmup } from '@/composables/useAuthBootstrap.js';

/**
 * @typedef {'unknown'|'ok'|'blocked'|'needsConsent'|'ephemeral'} StorageConsentStatus
 */

/** @type {import('vue').Ref<StorageConsentStatus>} */
const storageStatus = ref('unknown');

const accepting = ref(false);
/**
 * 全屏受限：仅手动 reveal（兼容旧入口）；进站 / 登录默认不再自动弹出。
 * 中长期 ephemeral 会话替代硬拦。
 */
const blockedFullscreenVisible = ref(false);
/** @type {import('vue').Ref<null | (() => void | Promise<void>)>} */
const pendingResume = ref(null);
let bootstrapped = false;

function syncStatusFromProbe() {
  const probe = probeLocalStorage();
  if (!probe.ok) {
    // 不可写 ≠ 不可用：允许 ephemeral 登录
    storageStatus.value = hasEssentialStorageConsent() ? 'ephemeral' : 'needsConsent';
    return probe;
  }
  storageStatus.value = hasEssentialStorageConsent() ? 'ok' : 'needsConsent';
  return probe;
}

/**
 * App 进站探测一次：只同步状态，从不自动全屏封站。
 * 必要存储同意在登录点击手势内隐式完成。
 */
export function bootstrapStorageGate() {
  if (bootstrapped) return syncStatusFromProbe();
  bootstrapped = true;
  const probe = syncStatusFromProbe();
  blockedFullscreenVisible.value = false;
  return probe;
}

/**
 * 登录闸门：必要本地存储同意与探测。
 * UI：默认不封站；localStorage 失败时仍可 ephemeral 登录。
 */
export function useStorageConsent() {
  const isBlocked = computed(
    () => storageStatus.value === 'blocked' || storageStatus.value === 'ephemeral'
  );
  const isEphemeral = computed(() => storageStatus.value === 'ephemeral');
  const needsConsent = computed(() => storageStatus.value === 'needsConsent');
  const hasConsent = computed(
    () =>
      hasEssentialStorageConsent() &&
      (storageStatus.value === 'ok' || storageStatus.value === 'ephemeral')
  );
  /** @deprecated 产品已取消底条；恒 false */
  const showConsentBanner = computed(() => false);
  const showBlockedFullscreen = computed(() => blockedFullscreenVisible.value);

  function refreshProbe() {
    return syncStatusFromProbe();
  }

  /**
   * @returns {{ ok: true, ephemeral?: boolean } | { ok: false, reason: string, ephemeral?: boolean }}
   */
  function checkReadyForLogin() {
    const result = requireStorageForLogin();
    if (result.ok) {
      storageStatus.value = result.ephemeral ? 'ephemeral' : 'ok';
      return result;
    }
    if (result.reason === 'needsConsent') {
      storageStatus.value = 'needsConsent';
    }
    return result;
  }

  /** @deprecated 硬封站仅兼容旧调用；新路径请用 ephemeral 提示 */
  function revealBlockedFullscreen() {
    blockedFullscreenVisible.value = true;
    pendingResume.value = null;
    storageStatus.value = 'blocked';
  }

  /**
   * @deprecated 底条已取消
   * @param {null | (() => void | Promise<void>)} [resume]
   */
  function requestConsentBanner(resume = null) {
    const probe = syncStatusFromProbe();
    if (!probe.ok) {
      storageStatus.value = 'ephemeral';
      if (resume) pendingResume.value = resume;
      return;
    }
    if (resume) pendingResume.value = resume;
    blockedFullscreenVisible.value = false;
  }

  function clearPendingResume() {
    pendingResume.value = null;
  }

  /**
   * 必须在用户点击手势中调用：尝试解锁存储 → 记同意 → Privy 预热。
   * localStorage 失败时仍返回 ok（ephemeral），不拦登录。
   * @param {{ runResume?: boolean, deferPrivyWarmup?: boolean }} [options]
   * @returns {Promise<{ ok: true, ephemeral?: boolean } | { ok: false, reason: string }>}
   */
  async function acceptEssentialStorage({ runResume = true, deferPrivyWarmup = false } = {}) {
    if (accepting.value) {
      return { ok: false, reason: 'busy' };
    }
    accepting.value = true;
    try {
      const access = await ensureStorageAccess();
      markEssentialStorageConsent();
      const ephemeral = !access.ok;
      storageStatus.value = ephemeral ? 'ephemeral' : 'ok';
      blockedFullscreenVisible.value = false;

      const warmup = startPrivyWarmup({ force: true }).catch((err) => {
        // Privy 已 memory 兜底；存储类错误不再视为登录失败
        if (!isStorageBlockedError(err)) {
          console.warn('[storage-consent] privy warmup failed', err);
        }
      });
      if (!deferPrivyWarmup) {
        await warmup;
      }

      if (runResume) {
        const resume = pendingResume.value;
        pendingResume.value = null;
        if (typeof resume === 'function') {
          try {
            await resume();
          } catch (err) {
            console.warn('[storage-consent] pending resume failed', err);
          }
        }
      } else {
        pendingResume.value = null;
      }

      return { ok: true, ephemeral };
    } finally {
      accepting.value = false;
    }
  }

  /** @deprecated 底条已取消 */
  function dismissEssentialStorage() {
    pendingResume.value = null;
  }

  function retryBlockedProbe() {
    const probe = syncStatusFromProbe();
    if (probe.ok) {
      blockedFullscreenVisible.value = false;
    }
    return probe;
  }

  const showLoginStorageCheckbox = computed(() => false);
  const hasDismissedConsent = computed(() => false);

  return {
    storageStatus,
    accepting,
    isBlocked,
    isEphemeral,
    needsConsent,
    hasConsent,
    showConsentBanner,
    showBlockedFullscreen,
    showLoginStorageCheckbox,
    hasDismissedConsent,
    refreshProbe,
    checkReadyForLogin,
    acceptEssentialStorage,
    dismissEssentialStorage,
    requestConsentBanner,
    revealBlockedFullscreen,
    clearPendingResume,
    retryBlockedProbe,
    isStorageBlockedError,
  };
}
